import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Download, Loader2, CheckCircle2, XCircle, FileArchive } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import api from '../services/api';

export const ExportProgress = ({ 
  jobId, 
  isOpen, 
  onClose, 
  title = "Export dokumentů",
  onComplete 
}) => {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  const fetchJobStatus = useCallback(async () => {
    if (!jobId) return;
    
    try {
      const response = await api.get(`/export/jobs/${jobId}`);
      setJob(response.data);
      
      if (response.data.status === 'completed' && onComplete) {
        onComplete(response.data);
      }
    } catch (err) {
      console.error('Error fetching job status:', err);
      setError('Nepodařilo se načíst stav exportu');
    }
  }, [jobId, onComplete]);

  useEffect(() => {
    if (!jobId || !isOpen) return;
    
    // Initial fetch
    fetchJobStatus();
    
    // Poll every 1 second while processing
    const interval = setInterval(() => {
      if (job?.status === 'completed' || job?.status === 'failed') {
        clearInterval(interval);
        return;
      }
      fetchJobStatus();
    }, 1000);
    
    return () => clearInterval(interval);
  }, [jobId, isOpen, fetchJobStatus, job?.status]);

  const handleDownload = async () => {
    if (!jobId) return;
    
    try {
      const response = await api.get(`/export/jobs/${jobId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', job?.file_name || 'export.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Nepodařilo se stáhnout soubor');
    }
  };

  const getStatusIcon = () => {
    if (!job) return <Loader2 className="w-8 h-8 animate-spin text-blue-500" />;
    
    switch (job.status) {
      case 'pending':
        return <Loader2 className="w-8 h-8 animate-spin text-slate-400" />;
      case 'processing':
        return <Loader2 className="w-8 h-8 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle2 className="w-8 h-8 text-green-500" />;
      case 'failed':
        return <XCircle className="w-8 h-8 text-red-500" />;
      default:
        return <FileArchive className="w-8 h-8 text-slate-400" />;
    }
  };

  const getStatusText = () => {
    if (!job) return 'Načítání...';
    
    switch (job.status) {
      case 'pending':
        return 'Čekání na zpracování';
      case 'processing':
        return 'Zpracovává se...';
      case 'completed':
        return 'Export dokončen';
      case 'failed':
        return 'Export selhal';
      default:
        return job.status;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileArchive className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {job?.export_type || 'Příprava exportu dokumentů'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-6">
          {/* Status Icon */}
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          
          {/* Status Text */}
          <p className="text-center font-medium text-slate-700 mb-2">
            {getStatusText()}
          </p>
          
          {/* Progress Message */}
          {job?.message && (
            <p className="text-center text-sm text-slate-500 mb-4">
              {job.message}
            </p>
          )}
          
          {/* Progress Bar */}
          {(job?.status === 'pending' || job?.status === 'processing') && (
            <div className="px-4">
              <Progress value={job?.progress || 0} className="h-2" />
              <p className="text-center text-xs text-slate-400 mt-2">
                {job?.progress || 0}%
              </p>
            </div>
          )}
          
          {/* Error Message */}
          {job?.status === 'failed' && job?.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{job.error}</p>
            </div>
          )}
          
          {/* General Error */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {/* Download Button */}
          {job?.status === 'completed' && (
            <div className="mt-6 flex justify-center">
              <Button 
                onClick={handleDownload}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Stáhnout {job.file_name}
              </Button>
            </div>
          )}
        </div>
        
        {/* Close Button */}
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            {job?.status === 'completed' ? 'Zavřít' : 'Zrušit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportProgress;
